import { queryOptions } from '@tanstack/react-query';
import { resourceKeys } from '@aimmo/ui';
import { api, assertOk } from './api.js';

/**
 * Every hand-written query key in the SPA, in one tree.
 *
 * The resource kit derives its own keys (`resourceKeys`); these are the rest.
 * They used to be literals scattered over twenty-five files, in two namespaces
 * at once (`'platform-agencies'` beside `['platform', …]`), and the same query
 * existed under the same key with two different fetches. A key here is the
 * only way to name a query, so an invalidation reaches what it means to and an
 * invalidation by prefix (`queryKeys.platform.all`) reaches everything below.
 */
export const queryKeys = {
  agency: ['agency'] as const,
  agencySendingDomain: ['agency', 'sending-domain'] as const,
  agencyUsers: ['agency-users'] as const,
  dashboard: ['dashboard', 'summary'] as const,

  activities: {
    all: ['activities'] as const,
    subject: (subjectType: string, subjectId: string) =>
      ['activities', subjectType, subjectId] as const,
    calendar: (month: string) => ['activities', 'calendar', month] as const,
  },

  contactEmailStatus: (contactId: string) =>
    [...resourceKeys.all('contacts'), contactId, 'email-status'] as const,
  demandMatches: (demandId: string) =>
    [...resourceKeys.all('demands'), 'matches', demandId] as const,
  propertyDemands: (propertyId: string) =>
    [...resourceKeys.all('properties'), 'demands', propertyId] as const,

  whatsapp: {
    all: ['whatsapp'] as const,
    account: ['whatsapp', 'account'] as const,
    conversations: ['whatsapp', 'conversations'] as const,
    threads: ['whatsapp', 'thread'] as const,
    thread: (conversationId: string) => ['whatsapp', 'thread', conversationId] as const,
  },

  campaigns: {
    all: ['campaigns'] as const,
    detail: (id: string) => ['campaigns', id] as const,
    recipients: (id: string) => ['campaigns', id, 'recipients'] as const,
  },
  segments: {
    options: [...resourceKeys.all('segments'), 'options'] as const,
    preview: (definition: string) =>
      [...resourceKeys.all('segments'), 'preview', definition] as const,
  },
  /**
   * Every template, for the campaign pickers — one query for both channels,
   * each picker selecting its own. Under the resource's own key, so editing a
   * template refreshes the pickers too.
   */
  messageTemplateOptions: [...resourceKeys.all('message-templates'), 'options'] as const,

  assistant: {
    sessions: ['assistant', 'sessions'] as const,
    session: (id: string) => ['assistant', 'session', id] as const,
    models: ['assistant', 'models'] as const,
    usage: ['assistant', 'usage'] as const,
    records: (refs: string) => ['assistant', 'records', refs] as const,
  },

  platform: {
    all: ['platform'] as const,
    agencies: ['platform', 'agencies'] as const,
    agency: (id: string) => ['platform', 'agencies', id] as const,
    agencySendingDomain: (id: string) => ['platform', 'sending-domain', id] as const,
    agencyDeletions: ['platform', 'agency-deletions'] as const,
    agencyDeletionPreview: (id: string) => ['platform', 'agency-deletion-preview', id] as const,
    agencyMetrics: (id: string) => ['platform', 'agency-metrics', id] as const,
    agencyFeatureUsage: (id: string) => ['platform', 'agency-feature-usage', id] as const,
    slugSuggestion: (name: string) => ['platform', 'slug-suggestion', name] as const,
    users: ['platform', 'users'] as const,
    websiteLeads: ['platform', 'website-leads'] as const,
    assistant: {
      models: ['platform', 'assistant', 'models'] as const,
      agencyModels: (id: string) => ['platform', 'assistant', 'agency-models', id] as const,
      credential: (id: string) => ['platform', 'assistant', 'credential', id] as const,
      reports: ['platform', 'assistant', 'reports'] as const,
      report: (id: string) => ['platform', 'assistant', 'reports', id] as const,
      views: ['platform', 'assistant', 'views'] as const,
      prompts: ['platform', 'assistant', 'prompts'] as const,
      settings: ['platform', 'assistant', 'settings'] as const,
      usage: ['platform', 'assistant', 'usage'] as const,
    },
  },
};

/*
 * The queries more than one screen asks: one definition each, so two screens
 * on the same key can never disagree about what is stored under it.
 */

/** The signed-in user's agency: settings, and the clock campaigns are scheduled on. */
export const agencyQuery = queryOptions({
  queryKey: queryKeys.agency,
  queryFn: async ({ signal }) => assertOk(await api.agency.get({ fetchOptions: { signal } })).body,
});

/**
 * The agency's WhatsApp number. While an import runs it is asked again every
 * ten seconds (M8.1): the realtime event is the fast path, but a socket that
 * drops takes the progress with it and leaves a frozen figure on screen.
 */
export const whatsappAccountQuery = queryOptions({
  queryKey: queryKeys.whatsapp.account,
  queryFn: async ({ signal }) =>
    assertOk(await api.whatsapp.get({ fetchOptions: { signal } })).body,
  refetchInterval: (query) => (query.state.data?.history?.state === 'running' ? 10_000 : false),
});

export const assistantSessionsQuery = queryOptions({
  queryKey: queryKeys.assistant.sessions,
  queryFn: async ({ signal }) =>
    assertOk(await api.assistant.sessions({ fetchOptions: { signal } })).body,
});

/** What this agency may run the assistant on (M15.2). */
export const assistantModelsQuery = queryOptions({
  queryKey: queryKeys.assistant.models,
  queryFn: async ({ signal }) =>
    assertOk(await api.assistant.models({ fetchOptions: { signal } })).body,
});

export const platformAgenciesQuery = queryOptions({
  queryKey: queryKeys.platform.agencies,
  queryFn: async ({ signal }) =>
    assertOk(await api.platform.listAgencies({ fetchOptions: { signal } })).body,
});

/** Deletions not yet `done`, including those whose agency row is already gone (ADR-0020). */
export const platformUnfinishedDeletionsQuery = queryOptions({
  queryKey: queryKeys.platform.agencyDeletions,
  queryFn: async ({ signal }) =>
    assertOk(await api.platform.listUnfinishedAgencyDeletions({ fetchOptions: { signal } })).body,
});

/** The operator's model registry, which every agency's grant is chosen from. */
export const platformAssistantModelsQuery = queryOptions({
  queryKey: queryKeys.platform.assistant.models,
  queryFn: async ({ signal }) =>
    assertOk(await api.platform.assistantModels({ fetchOptions: { signal } })).body,
});
